import { Component, ViewEncapsulation, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { debounceTime, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { UrlStateService } from 'src/shared/services/url-state.service';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { Genome } from 'src/providers/genome/genome';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { Client } from 'src/providers/client/client';
import { scrollContentWidth } from 'src/shared/components/scroll-wrapper/scroll-wrapper.component';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { AssetsQueriesService } from 'src/shared/services/assets-queries.service';
import { AddressesQueriesService } from 'src/shared/services/addresses-queries.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';

import { AddRoomItemPage } from 'src/pages/more/edit-address/add-room-item/add-room-item';
import { ItemDetailsPage } from 'src/pages/more/edit-address/room-item-details/room-item-details';
import { restockingOfficeMockData } from 'src/shared/constants/onboarding/restocking';
import { restockingMockDataResidential } from 'src/shared/constants/onboarding/restocking-residential';

@Component({
    templateUrl: './assets.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./assets.scss'],
    standalone: false
})
export class AssetsPage implements OnInit, OnDestroy {
  private readonly assetsQueries = inject(AssetsQueriesService);
  private readonly addressesQueries = inject(AddressesQueriesService);

  panelClosedSubscription: Subscription;
  shownRows: any = [];
  allRows: any = [];
  readonly desktopWidthContent: string = scrollContentWidth.LARGE;
  form: UntypedFormGroup;
  headers: any = ['Item', 'Property', 'Year Installed', 'last-column'];
  showLoadingSpinner: boolean;
  loaded: boolean;
  addressResponse: any;
  addressId: any;
  addressItems: any;
  propertyForm: UntypedFormGroup;
  showAboutThisPage: boolean;
  totalRows: number;
  totalAllRows: number;
  pageIndex = 1;
  searchTerm: string;
  searchPageIndex: number;
  @ViewChild('tidyTable') tidyTable: any;
  pendingAssetId: number | null = null;
  pendingAddressId: number | null = null;

  constructor(
    private client: Client,
    private currentAddress: CurrentAddress,
    private fb: UntypedFormBuilder,
    private genome: Genome,
    private navCtrl: CustomNavController,
    private aboutThisPageService: AboutThisPageService,
    private rightSidePanelService: RightSidePanelService,
    private route: ActivatedRoute,
    private storage: TidyStorage,
    private urlStateService: UrlStateService,
    private util: Util,
    public windowService: WindowService,
    private demoBlock: DemoBlockService,
  ) {
    this.form = this.fb.group({
      search: [''],
    });
    this.propertyForm = this.fb.group({
      property: ['', Validators.required],
    });
    this.form.get('search')?.valueChanges.pipe(
      debounceTime(300)
    ).subscribe(value => this.updateSearch({ search: value }));
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('maintenance');
  }

  async ngOnInit() {
    try {
      this.route.queryParams.pipe(take(1)).subscribe(params => {
        if (params['assetId'] && params['addressId']) {
          this.pendingAssetId = Number(params['assetId']);
          this.pendingAddressId = Number(params['addressId']);
        }
      });
      this.assetsQueries.setConsumable(false);
      this.assetsQueries.resetPagination();
      this.loadPage();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async loadPage(showLoadingSpinner = false, setStoredRoute = true) {
    if (showLoadingSpinner) {
      this.showLoadingSpinner = true;
      this.cleanup();
    }
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('maintenance');
    await this.getPageData();
    this.showLoadingSpinner = false;
    this.loaded = true;
    if (this.pendingAssetId && this.pendingAddressId && this.allRows?.length > 0) {
      const asset = this.allRows.find(row => row.id === this.pendingAssetId);
      if (asset) {
        this.goToRoomItemPage(this.findOriginalObject(asset));
      }
      this.pendingAssetId = null;
      this.pendingAddressId = null;
    }
    if (this.windowService.isDesktopRes && setStoredRoute) {
      this.storage.setStoredRoute('/assets').then(async () => {
        this.pageIndex = 1;
        this.searchTerm = '';
        this.form.get('search')?.setValue('');
        this.searchPageIndex = 1;
        await this.loadPage(true, true);
      });
    }
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.assetsQueries.invalidateAssets();
    this.assetsQueries.resetPagination();
    this.pageIndex = 1;
    this.searchPageIndex = 1;
    this.searchTerm = '';
    this.form.get('search')?.setValue('');
    this.shownRows = [];
    this.allRows = [];
    this.loadPage(true, false);
  }

  ngOnDestroy() {
    this.cleanup();
  }

  cleanup() {
    if (this.panelClosedSubscription) {
      this.storage.delete('shouldReloadPageAfterClosingSideRail');
      this.panelClosedSubscription.unsubscribe();
    }
  }

  async getPageData() {
    try {
      await this.getPropertyItems();
      this.propertyForm.get('property').setValue('all');
      this.buildHeaders();
      await this.getMaintenanceItems();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  async getPropertyItems() {
    this.addressResponse = await this.addressesQueries.getAddresses();
    if (this.addressResponse.length === 0) {
      return [];
    }
    this.addressId = this.client.getSelectedAddressId(this.addressResponse);
    this.addressItems = this.client.parseAddressList(this.addressResponse, true);
    this.addressItems.unshift({
      value: 'all',
      viewValue: 'All Properties'
    })
    this.addressItems.pop();
  }

  changeProperty() {
    if (this.propertyForm.value.property === 'all') {
      this.shownRows = this.allRows;
    } else {
      this.shownRows = this.allRows.filter((row) => {
        if (row['Property'].addressId === this.propertyForm.value.property) {
          return row;
        }
      });
    }
  }

  getPropertyName(address) {
    if (address.address_name) {
      return address.address_name;
    } else if (address.address2 && address.address2 !== '') {
      return `${address.address1}, ${address.address2}`;
    } else {
      return address.address1;
    }
  }

  buildHeaders() {
    if (this.windowService.isDesktopRes) {
      this.headers = [
        'Item',
        'Property',
        'Year Installed',
        'last-column'
      ];
    } else {
      this.headers = [
        'Item',
        'Property',
        'last-column'
      ];
    }
  }

  goToRoomItemPage(object) {
    if (this.showAboutThisPage) {
      return;
    }
    this.urlStateService.updateQueryParams({ addressId: object.address.id, assetId: object.id });
    const params = {
      addressId: object.address.id,
      itemId: object.id,
      cameFromEditItem: false,
      cameFromRoute: 'assets'
    };
    localStorage.setItem('roomItemBackPage', 'assets');
    this.goToPage(`item-details/${object.id}`, params, ItemDetailsPage);
  }

  goToPropertyPage(addressId) {
    if (this.showAboutThisPage) {
      return;
    }
    this.navCtrl.navigateForward(`properties?propertyId=${addressId}`);
  }

  sortChanged(sort) {
    if (this.showAboutThisPage) {
      return;
    }
    if (sort.direction === 'asc' || sort.direction === '') {
      this.allRows.sort((a, b) => a[sort.active].value > b[sort.active].value ? 1 : -1);
      this.shownRows = this.allRows;
    } else {
      this.allRows.sort((a, b) => a[sort.active].value < b[sort.active].value ? 1 : -1);
      this.shownRows = this.allRows;
    }
  }

  goToAddRoomItemPage() {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    if (localStorage.getItem('hasAtLeastOneAddress') !== 'true') {
      return this.util.showError('Please add a property to add an asset');
    }
    const params = {
      showAddressSelect: true,
      showConsumables: false
    }
    this.goToPage('add-item', params, AddRoomItemPage);
  }

  async goToPage(url, params, component) {
    if (this.showAboutThisPage) {
      return;
    }
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(take(1)).subscribe(async () => {
        this.urlStateService.updateQueryParams({ addressId: null, assetId: null });
        const shouldReloadPageAfterClosingSideRail = await this.storage.retrieve('shouldReloadPageAfterClosingSideRail');
        if (shouldReloadPageAfterClosingSideRail) {
          this.showLoadingSpinner = true;
          this.pageIndex = 1;
          this.searchPageIndex = 1;
          this.searchTerm = '';
          this.form.get('search')?.setValue('');
          this.assetsQueries.invalidateAssets();
          this.assetsQueries.resetPagination();
          await this.getPageData();
          this.showLoadingSpinner = false;
          await this.storage.delete('shouldReloadPageAfterClosingSideRail');
        }
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  onTableColumnClick(event: {column: string, element: any}) {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.demoBlock.block()) { return; }
    this.goToRoomItemPage(this.findOriginalObject(event.element));
  }

  findOriginalObject(element: any) {
    const address = this.addressResponse.find(addr => addr.id === element['Property'].addressId);
    return {
      address,
      id: element.id
    };
  }

  async updateSearch({search}) {
    this.searchTerm = search.toLowerCase();
    this.searchPageIndex = 1;
    this.assetsQueries.setSearch(this.searchTerm);
    if (search.length < 2) {
      this.totalRows = this.totalAllRows;
      return this.shownRows = this.allRows;
    }
    this.showLoadingSpinner = true;
    await this.getMaintenanceItems();
    this.showLoadingSpinner = false;
  }

  async pageChange() {
    if (this.showAboutThisPage) {
      return;
    }
    if (this.searchTerm) {
      this.searchPageIndex += 1;
    } else {
      this.pageIndex += 1;
    }
    this.tidyTable.isLoading = true;
    await this.getMaintenanceItems(true);
    this.tidyTable.isLoading = false;
  }

  async getMaintenanceItems(isPageChange = false): Promise<void> {
    try {
      let roomObjects = [];
      if (this.showAboutThisPage) {
        const accountType = localStorage.getItem('accountType');
        const isCompany = accountType === 'company';
        roomObjects = isCompany ? restockingOfficeMockData : restockingMockDataResidential;
        this.totalRows = roomObjects.length;
      }
      if (!this.showAboutThisPage) {
        const page = this.searchTerm ? this.searchPageIndex : this.pageIndex;
        const roomObjectsResp = await this.assetsQueries.getPage(page);
        roomObjects = roomObjectsResp.body;
        this.totalRows = roomObjectsResp.totalRecords;
      }
      // The shared demo account has no room-objects of its own; if the demo data
      // layer didn't supply any, fall back to curated sample data so the page
      // never renders blank in a demo session.
      if (this.demoBlock.isDemo && !roomObjects?.length) {
        const isCompany = localStorage.getItem('accountType') === 'company';
        roomObjects = isCompany ? restockingOfficeMockData : restockingMockDataResidential;
        this.totalRows = roomObjects.length;
      }
      const rows = this.buildRows(roomObjects);
      if (isPageChange) {
        this.shownRows = [
          ...this.shownRows,
          ...rows
        ];
      } else {
        this.shownRows = rows;
      }
      if (!this.searchTerm) {
        this.allRows = this.shownRows;
        this.totalAllRows = this.totalRows;
      }
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

  buildRows(roomObjects) {
    const rows = [];
    roomObjects.map((object) => {
      const cell: any = {
        'Item': {
          value: object.name,
          action: null,
          icon: object?.model?.category?.default_icon_url || 'assets/svg/home-outline.svg'
        },
        'Property': {
          value: this.getPropertyName(object.address),
          action: this.goToPropertyPage.bind(this, object.address.id),
          addressId: object.address.id,
          color: this.addressResponse.find((address) => address.id === object.address.id)?.color || '#1E2125'
        },
        'last-column': {
          value: '',
          action: null,
          hasChevron: true
        },
        id: object.id
      };
      if (this.windowService.isDesktopRes) {
        cell['Year Installed'] = {
          value: Genome.getInstallationYear(object.installation_date),
          action: null
        };
      }
      rows.push(cell);
    });
    return rows;
  }

  async exportCSV() {
    if (this.showAboutThisPage) {
      return;
    }
    if (!this.tidyTable) {
      this.util.showError('No data to export', 5000);
      return;
    }
    try {
      const loadingIndicator = await this.util.showLoading({message: 'Preparing export...'});
      const roomObjectsResp = await this.assetsQueries.getAssets({
        page: 1,
        perPage: this.totalRows,
        consumable: false,
        search: this.searchTerm || undefined,
      });
      const allRoomObjects = roomObjectsResp.body;
      const allRows = this.buildRows(allRoomObjects);
      const exportDataSource = new MatTableDataSource(allRows);
      this.tidyTable.exportCsv(exportDataSource);
      loadingIndicator?.dismiss();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
  }

}
